import React, { useEffect, useRef, useState } from 'react';
import { useCart } from '../context/CartContext';
import { api, formatCOP, imageUrl } from '../services/api';
import CachedProductImage from './CachedProductImage';
import CheckoutForm, { buildWebPedidoPayload } from './CheckoutForm';
import { getCartSessionId } from '../utils/cartSession';

function newRequestId() {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  return `web-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
}

export default function CartDrawer({ open, onClose }) {
  const { state, dispatch, subtotal, totalItems } = useCart();
  const [step, setStep] = useState('cart'); // cart | checkout | done
  const [sending, setSending] = useState(false);
  const [error, setError] = useState('');
  const [pedidoCreado, setPedidoCreado] = useState(null);
  const clientRequestIdRef = useRef(null);
  const { items, checkout } = state;

  useEffect(() => {
    if (!open) return undefined;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = prev;
    };
  }, [open]);

  useEffect(() => {
    if (!open) {
      setError('');
      if (step === 'done') {
        setStep('cart');
        setPedidoCreado(null);
      }
    }
  }, [open, step]);

  function goCheckout() {
    setError('');
    if (!items.length) return;
    if (!clientRequestIdRef.current) {
      clientRequestIdRef.current = newRequestId();
    }
    setStep('checkout');
  }

  async function enviarPedido() {
    setError('');
    if (!checkout.cliente_nombre.trim()) {
      setError('Escribe tu nombre o el de la tienda');
      return;
    }
    if (!checkout.cliente_telefono.trim()) {
      setError('Escribe un teléfono de contacto');
      return;
    }
    if (!items.length) {
      setError('Agrega productos al carrito');
      return;
    }

    if (!clientRequestIdRef.current) {
      clientRequestIdRef.current = newRequestId();
    }

    setSending(true);
    try {
      const payload = {
        ...buildWebPedidoPayload(checkout, items),
        client_request_id: clientRequestIdRef.current,
        cart_session_id: getCartSessionId(),
      };
      const res = await api.crearPedido(payload);
      clientRequestIdRef.current = null;
      setPedidoCreado(res.data);
      dispatch({ type: 'CLEAR' });
      setStep('done');
    } catch (err) {
      setError(err.message || 'No se pudo enviar el pedido');
    } finally {
      setSending(false);
    }
  }

  function cerrarTodo() {
    setStep('cart');
    setPedidoCreado(null);
    setError('');
    clientRequestIdRef.current = null;
    onClose();
  }

  const title =
    step === 'checkout' ? 'Finalizar pedido' : step === 'done' ? '¡Pedido enviado!' : 'Carrito de compras';

  return (
    <>
      <div className={`cart-backdrop ${open ? 'open' : ''}`} onClick={cerrarTodo} />
      <aside
        className={`cart-drawer ${open ? 'open' : ''} ${step !== 'cart' ? 'cart-drawer--wide' : ''}`}
        aria-hidden={!open}
      >
        <div className="cart-drawer-head">
          <div className="cart-drawer-head-text">
            {step === 'checkout' ? (
              <button type="button" className="cart-back" onClick={() => setStep('cart')}>
                ← Carrito
              </button>
            ) : null}
            <h2>{title}</h2>
            {step === 'cart' && totalItems > 0 ? (
              <span className="cart-count-pill">{totalItems} ítems</span>
            ) : null}
          </div>
          <button type="button" onClick={cerrarTodo} aria-label="Cerrar">
            CERRAR
          </button>
        </div>

        {step === 'cart' ? (
          <>
            <div className="cart-drawer-body">
              {!items.length ? (
                <div className="cart-empty-wrap">
                  <p className="cart-empty">Tu carrito está vacío</p>
                  <button type="button" className="btn-ghost" onClick={onClose}>
                    Regresar a la tienda
                  </button>
                </div>
              ) : (
                items.map((item) => (
                  <div key={item.lineId || item.codigo} className="cart-line">
                    {item.imagen ? (
                      <CachedProductImage src={imageUrl(item.imagen)} alt="" loading="eager" />
                    ) : (
                      <div className="cart-line-img-empty" />
                    )}
                    <div className="cart-line-info">
                      <strong>{item.nombre}</strong>
                      <span>Cód. {item.codigo}</span>
                      {item.opcion_nombre ? (
                        <span className="cart-line-opcion">{item.opcion_nombre}</span>
                      ) : null}
                      <span>{formatCOP(item.precio)}</span>
                      <label className="cart-line-obs-field">
                        <span className="sr-only">Observaciones</span>
                        <input
                          type="text"
                          className="cart-line-obs-input"
                          placeholder="Observaciones (color, talla…)"
                          value={item.observaciones || ''}
                          onChange={(e) =>
                            dispatch({
                              type: 'SET_OBS',
                              lineId: item.lineId,
                              observaciones: e.target.value,
                            })
                          }
                        />
                      </label>
                    </div>
                    <div className="cart-line-qty">
                      <button
                        type="button"
                        onClick={() =>
                          dispatch({
                            type: 'SET_QTY',
                            lineId: item.lineId,
                            codigo: item.codigo,
                            qty: item.cantidad - 1,
                          })
                        }
                      >
                        −
                      </button>
                      <span>{item.cantidad}</span>
                      <button
                        type="button"
                        onClick={() =>
                          dispatch({
                            type: 'SET_QTY',
                            lineId: item.lineId,
                            codigo: item.codigo,
                            qty: item.cantidad + 1,
                          })
                        }
                      >
                        +
                      </button>
                    </div>
                  </div>
                ))
              )}
            </div>

            {items.length > 0 ? (
              <div className="cart-drawer-foot">
                <div className="cart-total-row">
                  <span>Total</span>
                  <strong>{formatCOP(subtotal)}</strong>
                </div>
                <button type="button" className="btn-checkout" onClick={goCheckout}>
                  Continuar pedido
                </button>
                <button type="button" className="btn-clear" onClick={() => dispatch({ type: 'CLEAR' })}>
                  Vaciar carrito
                </button>
              </div>
            ) : null}
          </>
        ) : null}

        {step === 'checkout' ? (
          <>
            <div className="cart-drawer-body cart-drawer-body--checkout">
              <div className="checkout-summary">
                <span>{totalItems} producto{totalItems === 1 ? '' : 's'}</span>
                <strong>{formatCOP(subtotal)}</strong>
              </div>
              <CheckoutForm />
              {error ? <p className="cart-error">{error}</p> : null}
            </div>
            <div className="cart-drawer-foot">
              <button
                type="button"
                className="btn-checkout"
                disabled={sending}
                onClick={enviarPedido}
              >
                {sending ? 'Enviando…' : 'Enviar pedido'}
              </button>
              <p className="checkout-foot-note">
                Llega a oficina (cartera) marcado como <strong>Tienda web</strong>, sin vendedor.
              </p>
            </div>
          </>
        ) : null}

        {step === 'done' ? (
          <div className="cart-drawer-body cart-success">
            <div className="cart-success-icon" aria-hidden>
              ✓
            </div>
            <h3>Pedido recibido</h3>
            <p>
              Tu pedido <strong>{pedidoCreado?.numero || ''}</strong> ya está en cartera.
              Te contactaremos para confirmarlo.
            </p>
            <button type="button" className="btn-checkout" onClick={cerrarTodo}>
              Seguir comprando
            </button>
          </div>
        ) : null}
      </aside>
    </>
  );
}
